<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { hasPressedCommand } from 'shared/helpers/KeyboardHelpers';
import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';

const props = defineProps({
  templatePayload: {
    type: Object,
    default: null,
  },
});

const { t } = useI18n();
const showGallery = ref(false);

const params = computed(() => props.templatePayload ?? null);

const headerComponent = computed(() =>
  params.value?.components?.find(c => (c.type || '').toLowerCase() === 'header')
);

const headerParams = computed(() => headerComponent.value?.parameters ?? []);

const templateName = computed(() => params.value?.name ?? '');

const textHeaderParam = computed(() =>
  headerParams.value.find(p => (p.type || '').toLowerCase() === 'text')
);

const mediaHeaderParam = computed(() =>
  headerParams.value.find(p =>
    ['image', 'video', 'document'].includes((p.type || '').toLowerCase())
  )
);

const locationHeaderParam = computed(() =>
  headerParams.value.find(p => (p.type || '').toLowerCase() === 'location')
);

const locationCoords = computed(() => {
  const loc = locationHeaderParam.value?.location;
  if (!loc?.latitude || !loc?.longitude) return null;
  return {
    lat: loc.latitude,
    lng: loc.longitude,
    name: loc.name,
    address: loc.address,
  };
});

const mapsSearchUrl = computed(() => {
  if (!locationCoords.value) return null;
  const q = encodeURIComponent(
    `${locationCoords.value.lat},${locationCoords.value.lng}`
  );
  return `https://www.google.com/maps/search/?api=1&query=${q}`;
});

const headerText = computed(() => textHeaderParam.value?.text ?? '');

const mediaType = computed(() =>
  mediaHeaderParam.value
    ? (mediaHeaderParam.value.type || '').toLowerCase()
    : null
);

const headerMediaUrl = computed(() => {
  if (!mediaHeaderParam.value || !mediaType.value) return null;
  const mediaData = mediaHeaderParam.value[mediaType.value];
  return mediaData?.link ?? null;
});

const fileName = computed(() => {
  if (!mediaHeaderParam.value || !mediaType.value) return '';
  const mediaData = mediaHeaderParam.value[mediaType.value];
  if (mediaData?.filename) return mediaData.filename;
  if (!headerMediaUrl.value) return '';
  const urlPath = headerMediaUrl.value.split('?')[0];
  const name = urlPath.substring(urlPath.lastIndexOf('/') + 1);
  return name || '';
});

const galleryAttachment = computed(() => ({
  data_url: headerMediaUrl.value,
  file_type: mediaType.value === 'image' ? 'image' : 'video',
}));

const isImage = computed(() => mediaType.value === 'image');
const isVideo = computed(() => mediaType.value === 'video');
const isDocument = computed(() => mediaType.value === 'document');

const onMediaClick = e => {
  if (hasPressedCommand(e)) {
    window.open(headerMediaUrl.value, '_blank');
    return;
  }
  if (isImage.value || isVideo.value) showGallery.value = true;
};

const hasContent = computed(
  () =>
    templateName.value ||
    headerText.value ||
    headerMediaUrl.value ||
    mapsSearchUrl.value
);
</script>

<template>
  <div v-if="hasContent" class="pb-2 mb-2 border-b border-n-strong">
    <div v-if="templateName" class="mb-2">
      <span class="font-semibold text-xs text-n-slate-11">
        {{ t('CONVERSATION.WHATSAPP_TEMPLATE_LABEL', { templateName }) }}
      </span>
    </div>

    <p v-if="headerText" class="text-sm text-n-slate-12 break-words mb-2">
      {{ headerText }}
    </p>

    <div
      v-if="headerMediaUrl && (isImage || isVideo)"
      class="relative rounded-lg overflow-hidden mb-2 cursor-pointer"
      @click="onMediaClick"
    >
      <img
        v-if="isImage"
        :src="headerMediaUrl"
        class="max-w-full rounded-lg skip-context-menu"
        alt=""
      />
      <video
        v-else-if="isVideo"
        :src="headerMediaUrl"
        class="max-w-full rounded-lg skip-context-menu"
        muted
        playsinline
      />
      <div
        v-if="fileName && (isImage || isVideo)"
        class="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs p-1 truncate rounded-b-lg"
      >
        {{ decodeURI(fileName) }}
      </div>
    </div>

    <a
      v-else-if="headerMediaUrl && isDocument"
      :href="headerMediaUrl"
      target="_blank"
      rel="noreferrer noopener nofollow"
      class="inline-flex items-center gap-2 rounded-lg border border-n-container bg-n-solid-3 px-3 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-3"
    >
      {{ decodeURI(fileName) || $t('CONVERSATION.DOWNLOAD') }}
    </a>

    <div
      v-else-if="mapsSearchUrl && locationCoords"
      class="mb-2 rounded-lg border border-n-container bg-n-solid-3 px-3 py-2 text-sm text-n-slate-12"
    >
      <p v-if="locationCoords.name" class="font-medium text-n-slate-12 mb-1">
        {{ locationCoords.name }}
      </p>
      <p v-if="locationCoords.address" class="text-n-slate-11 mb-1">
        {{ locationCoords.address }}
      </p>
      <p class="text-n-slate-11 mb-2">
        {{ locationCoords.lat }}, {{ locationCoords.lng }}
      </p>
      <a
        :href="mapsSearchUrl"
        target="_blank"
        rel="noreferrer noopener nofollow"
        class="text-n-teal-9 hover:underline"
      >
        {{ t('CONVERSATION.WHATSAPP_TEMPLATE_HEADER_LOCATION_LINK') }}
      </a>
    </div>

    <GalleryView
      v-if="showGallery && (isImage || isVideo)"
      v-model:show="showGallery"
      :attachment="galleryAttachment"
      :all-attachments="[]"
      @close="showGallery = false"
    />
  </div>
</template>
